import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import SearchHero from "@/components/SearchHero";
import ResultsHeader from "@/components/ResultsHeader";
import FilterBar from "@/components/FilterBar";
import ImageGrid from "@/components/ImageGrid";
import JsonViewer from "@/components/JsonViewer";
import PaginationBar from "@/components/PaginationBar";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [filters, setFilters] = useState({
    language: "", country: "", image_size: "",
    image_type: "", color: "", time_range: "",
  });

  const hasSearched = submittedQuery.length > 0;

  const doSearch = useCallback(async (q, p, f) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = { q: q.trim(), page: p };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/search`, { params });
      setResults(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erro na busca";
      setError(msg);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (searchQuery) => {
    const q = searchQuery || query;
    setSubmittedQuery(q);
    setPage(1);
    doSearch(q, 1, filters);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    doSearch(submittedQuery, newPage, filters);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (submittedQuery) {
      setPage(1);
      doSearch(submittedQuery, 1, newFilters);
    }
  };

  const clearFilters = () => {
    const empty = { language: "", country: "", image_size: "", image_type: "", color: "", time_range: "" };
    setFilters(empty);
    if (submittedQuery) {
      setPage(1);
      doSearch(submittedQuery, 1, empty);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white relative overflow-x-hidden w-full max-w-[100vw]">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-[#09090B]">
          <div className="h-full bg-[#007AFF] loading-bar" data-testid={HOME.loadingIndicator} />
        </div>
      )}

      <AnimatePresence mode="wait">
        {!hasSearched ? (
          <motion.div
            key="hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SearchHero query={query} setQuery={setQuery} onSearch={handleSearch} loading={loading} />
          </motion.div>
        ) : (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col min-h-screen"
          >
            <ResultsHeader query={query} setQuery={setQuery} onSearch={handleSearch} loading={loading} />
            <FilterBar filters={filters} onChange={handleFilterChange} onClear={clearFilters} />

            <div className={`flex-1 overflow-x-hidden transition-all duration-300 ${jsonOpen ? "sm:mr-[420px]" : ""}`}>
              {error && (
                <div data-testid={HOME.errorMessage} className="mx-3 sm:mx-6 mt-4 sm:mt-6 p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                  {error}
                </div>
              )}

              {results && results.results_count > 0 && (
                <>
                  <div className="px-3 sm:px-6 pt-4 sm:pt-5 pb-2 flex items-center justify-between">
                    <p data-testid={HOME.resultCount} className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.1em] text-white/40">
                      {results.results_count} resultados — pag {results.page}
                    </p>
                    <button
                      data-testid={HOME.jsonToggle}
                      onClick={() => setJsonOpen(!jsonOpen)}
                      className={`text-[10px] sm:text-xs font-mono uppercase tracking-[0.1em] transition-colors cursor-pointer flex items-center gap-1 sm:gap-1.5 px-2.5 py-1 rounded-lg border ${
                        jsonOpen
                          ? "text-white bg-[#007AFF] border-[#007AFF]"
                          : "text-[#007AFF] hover:text-[#007AFF]/80 border-[#007AFF]/30 hover:border-[#007AFF]/50"
                      }`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/></svg>
                      {jsonOpen ? "Ocultar JSON" : "Ver JSON"}
                    </button>
                  </div>
                  <ImageGrid results={results.results} loading={loading} />
                  <PaginationBar page={page} hasMore={results.has_more} onPageChange={handlePageChange} loading={loading} />
                </>
              )}

              {results && results.results_count === 0 && !loading && (
                <div data-testid={HOME.emptyState} className="flex flex-col items-center justify-center py-20 sm:py-32 text-white/40 px-4">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3 sm:mb-4 opacity-30">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                  </svg>
                  <p className="text-base sm:text-lg">Nenhum resultado encontrado</p>
                  <p className="text-xs sm:text-sm mt-1">Tente outra busca ou ajuste os filtros</p>
                </div>
              )}
            </div>

            <AnimatePresence>
              {jsonOpen && results && (
                <JsonViewer data={results} onClose={() => setJsonOpen(false)} />
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
